import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';

import Toggle from '../Toggle';
import { billingSubscriptionModifyRequest } from '../../sagas/billing/subscriptionModify/actions';

import './_pricingMatrix.scss';

const BILLING_PERIODS = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual'
};

const STARTER_FEATURES = [
  {
    enabled: true,
    title: 'Unlimited creator management tools',
    tags: ['Creator Discovery', 'Analytics', '1:1 Messaging', 'Gifting', 'Bonusing']
  },
  {
    enabled: false,
    title: 'Promotional push messages',
    description: 'Upgrade to reach creators via Discover carousel'
  },
  {
    enabled: false,
    title: 'Batch messaging',
    description: 'Messages sent one creator at a time'
  },
  {
    enabled: false,
    title: 'AI assistant',
    description: 'Unavailable on Free plan'
  }
];

const PRO_FEATURES = [
  {
    enabled: true,
    title: 'Unlimited creator management tools',
    tags: ['Creator Discovery', 'Analytics', '1:1 Messaging', 'Gifting', 'Bonusing']
  },
  {
    enabled: true,
    title: 'Promotional push messages',
    description: 'Blast sales & events to the Discover carousel'
  },
  {
    enabled: true,
    title: 'Auto-Discover talent',
    description: 'Automatically invite relevant creators to grow your network'
  },
  {
    enabled: true,
    title: 'Batch messaging',
    description: 'Reach your whole creator roster at once'
  },
  {
    enabled: true,
    title: 'AI assistant',
    description: 'Do it all, stress free! Up to 15M tokens/mo incl.'
  }
];

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.0003 1.66602C5.40033 1.66602 1.66699 5.39935 1.66699 9.99935C1.66699 14.5993 5.40033 18.3327 10.0003 18.3327C14.6003 18.3327 18.3337 14.5993 18.3337 9.99935C18.3337 5.39935 14.6003 1.66602 10.0003 1.66602ZM10.0003 16.666C6.32533 16.666 3.33366 13.6743 3.33366 9.99935C3.33366 6.32435 6.32533 3.33268 10.0003 3.33268C13.6753 3.33268 16.667 6.32435 16.667 9.99935C16.667 13.6743 13.6753 16.666 10.0003 16.666ZM13.8253 6.31602L8.33366 11.8077L6.17533 9.65768L5.00033 10.8327L8.33366 14.166L15.0003 7.49935L13.8253 6.31602Z" fill="#00B722"/>
  </svg>
);

const CrossIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.1587 6.66602L10.0003 8.82435L7.84199 6.66602L6.66699 7.84102L8.82533 9.99935L6.66699 12.1577L7.84199 13.3327L10.0003 11.1743L12.1587 13.3327L13.3337 12.1577L11.1753 9.99935L13.3337 7.84102L12.1587 6.66602ZM10.0003 1.66602C5.39199 1.66602 1.66699 5.39102 1.66699 9.99935C1.66699 14.6077 5.39199 18.3327 10.0003 18.3327C14.6087 18.3327 18.3337 14.6077 18.3337 9.99935C18.3337 5.39102 14.6087 1.66602 10.0003 1.66602ZM10.0003 16.666C6.32533 16.666 3.33366 13.6743 3.33366 9.99935C3.33366 6.32435 6.32533 3.33268 10.0003 3.33268C13.6753 3.33268 16.667 6.32435 16.667 9.99935C16.667 13.6743 13.6753 16.666 10.0003 16.666Z" fill="#F92B49"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.33301 4.66732H8.66634V6.00065H7.33301V4.66732ZM7.33301 7.33398H8.66634V11.334H7.33301V7.33398ZM7.99967 1.33398C4.31967 1.33398 1.33301 4.32065 1.33301 8.00065C1.33301 11.6807 4.31967 14.6673 7.99967 14.6673C11.6797 14.6673 14.6663 11.6807 14.6663 8.00065C14.6663 4.32065 11.6797 1.33398 7.99967 1.33398ZM7.99967 13.334C5.05967 13.334 2.66634 10.9407 2.66634 8.00065C2.66634 5.06065 5.05967 2.66732 7.99967 2.66732C10.9397 2.66732 13.333 5.06065 13.333 8.00065C13.333 10.9407 10.9397 13.334 7.99967 13.334Z" fill="white"/>
  </svg>
);

const renderFeature = (feature, variant) => {
  const isDark = variant === 'dark';
  const borderColor = isDark ? '#393D63' : '#E3E3E3';
  const titleColor = isDark ? 'white' : '#2A2E54';
  const descColor = isDark ? '#AEC0E5' : '#666A85';
  const tagBg = isDark ? '#343A71' : '#F7F8FA';
  const tagColor = isDark ? '#AEC0E5' : '#666A85';

  return (
    <div key={feature.title} style={{ alignSelf: 'stretch', paddingTop: 12, borderTop: `1px ${borderColor} solid`, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 5, display: 'flex' }}>
      <div style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 4, display: 'flex' }}>
        <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 4, display: 'inline-flex' }}>
          <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'flex' }}>
            {feature.enabled ? <CheckIcon /> : <CrossIcon />}
          </div>
          <div style={{ color: titleColor, fontSize: 14, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', wordWrap: 'break-word' }}>{feature.title}</div>
        </div>
        {feature.tags && (
          <div style={{ paddingLeft: 24, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex', flexWrap: 'wrap' }}>
            {feature.tags.map(tag => (
              <div key={tag} style={{ paddingLeft: 4, paddingRight: 4, background: tagBg, borderRadius: 2, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                <div style={{ color: tagColor, fontSize: 14, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', wordWrap: 'break-word' }}>{tag}</div>
              </div>
            ))}
          </div>
        )}
        {feature.description && (
          <div style={{ paddingLeft: 24, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
            <div style={{ color: descColor, fontSize: 14, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', wordWrap: 'break-word' }}>{feature.description}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const PricingMatrix = ({ currentTier = 'freemium' }) => {
  const dispatch = useDispatch();
  const [isAnnual, setIsAnnual] = useState(true);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const { loading } = useSelector(s => s.billingSubscriptionModify);
  const { data: accountData } = useSelector(s => s.account);
  const canUpgrade = accountData?.is_group_admin || false;
  const isMobile = useMediaQuery('(max-width:900px)');

  const handleUpgrade = () => {
    if (!canUpgrade) {
      window.location.href = 'mailto:?subject=Upgrade%20to%20Pro%20Request&body=Hi%2C%20I%20would%20like%20to%20request%20an%20upgrade%20to%20the%20Pro%20plan%20for%20our%20organization.';
      return;
    }
    setUpgradeLoading(true);

    const billingPeriod = isAnnual ? BILLING_PERIODS.ANNUAL : BILLING_PERIODS.MONTHLY;
    dispatch(billingSubscriptionModifyRequest({
      billingPeriod,
      onSuccess: data => {
        if (data.checkout_url) {
          window.open(data.checkout_url, '_self');
        }
      },
      onError: () => {
        setUpgradeLoading(false);
      }
    }));
  };

  const monthlyPrice = 300;
  const annualPrice = 250;
  const currentPrice = isAnnual ? annualPrice : monthlyPrice;

  return (
    <div style={{ paddingLeft: isMobile ? 0 : 16, paddingRight: isMobile ? 0 : 16, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 8, display: 'flex', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 10, display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>

        {/* Starter Plan */}
        <div style={{ alignSelf: 'stretch', padding: isMobile ? 20 : 32, background: 'white', borderRadius: 8, outline: '1px #666A85 solid', outlineOffset: -1, flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', display: 'flex', flex: 1 }}>
          <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 8, display: 'flex' }}>
            <div style={{ alignSelf: 'stretch', color: '#666A85', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '14px', letterSpacing: 0.24, wordWrap: 'break-word' }}>STARTER</div>
            <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
              <div style={{ justifyContent: 'flex-start', alignItems: 'flex-start', gap: 4, display: 'flex' }}>
                <div style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                  <div style={{ alignSelf: 'stretch', color: '#2A2E54', fontSize: 24, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '28px', wordWrap: 'break-word' }}>Free</div>
                  <div style={{ alignSelf: 'stretch', color: '#2A2E54', fontSize: 32, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '40px', wordWrap: 'break-word' }}>$0</div>
                </div>
                <div style={{ height: 59, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                  <div style={{ alignSelf: 'stretch', height: 50, justifyContent: 'flex-end', display: 'flex', flexDirection: 'column', color: '#666A85', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '14px', letterSpacing: 0.24, wordWrap: 'break-word' }}>/ month forever</div>
                </div>
              </div>
              <div style={{ padding: 8, background: '#1B1B1E', borderRadius: 4, justifyContent: 'center', alignItems: 'center', gap: 8, display: 'flex' }}>
                <InfoIcon />
                <div style={{ justifyContent: 'flex-end', display: 'flex', flexDirection: 'column', color: '#F7F8FA', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '14px', letterSpacing: 0.24, wordWrap: 'break-word' }}>10% Transaction Fee</div>
              </div>
            </div>
          </div>
          <div style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex', alignSelf: 'stretch' }}>
            {STARTER_FEATURES.map(feature => renderFeature(feature, 'light'))}
            <div style={{ alignSelf: 'stretch', height: 52, paddingLeft: 24, paddingRight: 24, paddingTop: 16, paddingBottom: 16, background: 'rgba(174, 192, 229, 0.50)', borderRadius: 6, justifyContent: 'center', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
              <div style={{ textAlign: 'center', color: 'rgba(42, 46, 84, 0.40)', fontSize: 16, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '20px', wordWrap: 'break-word' }}>Your current plan</div>
            </div>
          </div>
        </div>

        {/* Pro Plan */}
        <div style={{ padding: isMobile ? 20 : 32, background: '#2A2E54', borderRadius: 8, outline: '1px #5B73A5 solid', outlineOffset: -1, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex', alignSelf: 'stretch', flex: 1 }}>
          <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 8, display: 'flex' }}>
            <div style={{ alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 12, display: 'flex' }}>
              <div style={{ alignSelf: 'stretch', justifyContent: 'center', alignItems: 'center', gap: 10, display: 'inline-flex' }}>
                <div style={{ flex: '1 1 0', color: '#F7F8FA', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '14px', letterSpacing: 0.24, wordWrap: 'break-word' }}>MOST POPULAR</div>
                <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'flex' }}>
                  <div style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-end', gap: 2, display: 'flex' }}>
                    <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 4, display: 'flex' }}>
                      <div style={{ color: '#AEC0E5', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>Billed:</div>
                      <div style={{ color: '#52B000', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '14px', letterSpacing: 0.24, wordWrap: 'break-word' }}>{isAnnual ? 'Annually' : 'Monthly'}</div>
                    </div>
                  </div>
                  <Toggle
                    checked={isAnnual}
                    onChange={() => setIsAnnual(!isAnnual)}
                    size="small"
                    className="pricing-billing-toggle"
                  />
                </div>
              </div>
              <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex' }}>
                <div style={{ justifyContent: 'flex-start', alignItems: 'flex-start', gap: 4, display: 'flex' }}>
                  <div style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                    <div style={{ alignSelf: 'stretch', color: 'white', fontSize: 24, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '28px', wordWrap: 'break-word' }}>Pro</div>
                    {isAnnual ? (
                      <div style={{ alignSelf: 'stretch' }}>
                        <span style={{ color: '#B3B3B3', fontSize: 32, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '40px', wordWrap: 'break-word' }}>$</span>
                        <span style={{ color: '#B3B3B3', fontSize: 32, fontFamily: 'Montserrat', fontWeight: 500, textDecoration: 'line-through', lineHeight: '40px', wordWrap: 'break-word' }}>{monthlyPrice}</span>
                      </div>
                    ) : (
                      <div style={{ alignSelf: 'stretch', color: 'white', fontSize: 32, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '40px', wordWrap: 'break-word' }}>${currentPrice}</div>
                    )}
                  </div>
                  {isAnnual && (
                    <div style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                      <div style={{ alignSelf: 'stretch', color: 'white', fontSize: 24, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '28px', wordWrap: 'break-word' }}>&nbsp;</div>
                      <div style={{ alignSelf: 'stretch', color: 'white', fontSize: 32, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '40px', wordWrap: 'break-word' }}>${currentPrice}</div>
                    </div>
                  )}
                  <div style={{ width: 76, height: 59, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                    <div style={{ alignSelf: 'stretch', height: 50, justifyContent: 'flex-end', display: 'flex', flexDirection: 'column', color: '#666A85', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '14px', letterSpacing: 0.24, wordWrap: 'break-word' }}>/ month</div>
                  </div>
                </div>
                <div style={{ padding: 8, background: '#1B1B1E', borderRadius: 4, flexDirection: 'column', justifyContent: 'center', alignItems: 'center', display: 'inline-flex' }}>
                  <div style={{ justifyContent: 'flex-end', display: 'flex', flexDirection: 'column', color: '#F7F8FA', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '14px', letterSpacing: 0.24, wordWrap: 'break-word' }}>0% Transaction Fee</div>
                </div>
              </div>
              {isAnnual ? (
                <div style={{ alignSelf: 'stretch', padding: 16, background: '#393D63', borderRadius: 8, outline: '1px #666A85 solid', outlineOffset: -1, flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 8, display: 'flex' }}>
                  <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                    <div style={{ color: '#52B000', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '14px', letterSpacing: 0.24, wordWrap: 'break-word' }}>ANNUAL REBATE</div>
                    <div style={{ padding: 4, background: '#97D066', borderRadius: 4, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                      <div style={{ color: '#193600', fontSize: 10, fontFamily: 'Montserrat', fontWeight: 700, letterSpacing: 0.20, wordWrap: 'break-word' }}>First 5 months</div>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>Get $300 bonus for every </span>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>$1,000</span>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}> loaded. Up to </span>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>$1,500</span>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}> total</span>
                  </div>
                </div>
              ) : (
                <div style={{ alignSelf: 'stretch', padding: 16, background: '#393D63', borderRadius: 8, outline: '1px #666A85 solid', outlineOffset: -1, flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 8, display: 'inline-flex' }}>
                  <div style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 8, display: 'inline-flex' }}>
                    <div style={{ color: '#52B000', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '14px', letterSpacing: 0.24, wordWrap: 'break-word' }}>MONTHLY REBATE</div>
                    <div style={{ padding: 4, background: '#5B73A5', borderRadius: 4, justifyContent: 'center', alignItems: 'center', gap: 10, display: 'flex' }}>
                      <div style={{ color: '#F7F8FA', fontSize: 10, fontFamily: 'Montserrat', fontWeight: 700, letterSpacing: 0.20, wordWrap: 'break-word' }}>First 3 months</div>
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>Get $300 bonus for every </span>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>$1,000</span>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}> loaded. Up to </span>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>$900</span>
                    <span style={{ color: 'white', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}> total. </span>
                    <span style={{ color: '#52B000', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>Switch to Annual for up to $1,500.</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div style={{ flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 16, display: 'flex', alignSelf: 'stretch' }}>
            {PRO_FEATURES.map(feature => renderFeature(feature, 'dark'))}
            <button
              onClick={handleUpgrade}
              disabled={loading || upgradeLoading || currentTier === 'paid'}
              style={{ alignSelf: 'stretch', height: 52, paddingLeft: 24, paddingRight: 24, paddingTop: 16, paddingBottom: 16, background: (loading || upgradeLoading || currentTier === 'paid') ? 'rgba(255,255,255,0.5)' : 'white', borderRadius: 6, justifyContent: 'center', alignItems: 'center', gap: 8, display: 'inline-flex', border: 'none', cursor: (loading || upgradeLoading || currentTier === 'paid') ? 'not-allowed' : 'pointer' }}
            >
              <div style={{ textAlign: 'center', color: '#2A2E54', fontSize: 16, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '20px', wordWrap: 'break-word' }}>
                {(loading || upgradeLoading) ? 'Processing...' : currentTier === 'paid' ? 'Your current plan' : !canUpgrade ? 'Ask your admin for the upgrade' : 'Upgrade to Pro'}
              </div>
            </button>
          </div>
        </div>

      </div>

      {/* Footer */}
      <div style={{ alignSelf: 'stretch', paddingTop: 16, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', gap: 20, display: 'flex' }}>
        <div style={{ alignSelf: 'stretch', textAlign: isMobile ? 'center' : 'left' }}>
          <span style={{ color: '#666A85', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 700, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>How the rebate works: </span>
          <span style={{ color: '#666A85', fontSize: 12, fontFamily: 'Montserrat', fontWeight: 500, lineHeight: '20px', letterSpacing: 0.24, wordWrap: 'break-word' }}>Get $300 back for every $1000 loaded. Total rebate capped at $900 (Monthly plan, first 3 months) or $1,500 (Annual plan, first 5 months). Credits are non-refundable and for platform use only.</span>
        </div>
      </div>
    </div>
  );
};

PricingMatrix.propTypes = {
  currentTier: PropTypes.oneOf(['freemium', 'paid']),
  canUpgrade: PropTypes.bool
};

export default PricingMatrix;
